import React, { MutableRefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { tokens, TranslatedToken, useTranslation } from '@shared/utils/i18n';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography
} from '@mui/material';
import { formatPhone } from '@shared/utils/helpers/formatString';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import {
  DataGridPro,
  GridCellModes,
  GridCellParams,
  GridColDef,
  GridEditInputCell,
  GridEditModes,
  GridRenderEditCellParams,
  GridRowEditStopParams,
  GridRowModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridValueFormatterParams,
  useGridApiContext,
  useGridApiRef
} from '@mui/x-data-grid-pro';
import { observer } from 'mobx-react-lite';
import {
  IOsiAbonentsWidgetViewModel,
  IOsiAbonentsWidgetViewModelToken
} from '@shared/types/mobx/widgets/OsiAbonentsWidget';
import { useInjection } from 'inversify-react';
import { Abonent } from '@shared/types/osi/abonents';
import _ from 'lodash';
import { notifications } from '@shared/utils/notifications';
import { createAbonent, getAbonent, updateAbonent } from '@shared/api/abonents';
import { formatAbonentRow } from '@widgets/osi/abonents/utils';
import logger from 'js-logger';
import { AreaTypeCodes } from '@shared/types/dictionaries';
import { clsx } from 'clsx';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';
import * as Yup from 'yup';
import { checkBIN, checkIIN } from '@shared/utils/helpers/validation/validateIDN';
import IMask from 'imask';
import { styled } from '@mui/material/styles';
import { ImportAbonentsButton } from '@widgets/osi/abonents/ui/ImportAbonentsButton';
// OSI-788: канонические русские значения признака владельца + перевод для отображения
import { OWNER_VALUES, isOwnerEmpty, translateOwner } from '@shared/utils/helpers/ownerType';
import { OsiAddAbonentButton } from './OsiAddAbonentButton';

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText
  }
}));

const RequiredEditCell = (props: GridRenderEditCellParams) => {
  const { t } = useTranslation();
  const { error, hasFocus, value, cellMode } = props;

  const isError = !!error || (hasFocus && cellMode === GridCellModes.Edit && !value);

  return (
    <StyledTooltip open={isError} title={t('abonents:validation.requiredField')}>
      <GridEditInputCell {...props} />
    </StyledTooltip>
  );
};

function renderRequiredEditCell(params: GridRenderEditCellParams) {
  return <RequiredEditCell {...params} />;
}

const checkRequiredCell = (params: GridCellParams, apiRef: MutableRefObject<GridApiPro>) => {
  const { cellMode, id, field } = params;

  if (cellMode !== GridCellModes.Edit) return '';

  const currentValue = apiRef.current.unstable_getRowWithUpdatedValuesFromCellEditing(id, field)[field];

  if (!currentValue) return clsx('required', 'Mui-error');

  return '';
};

// OSI-808: персистентность набора видимых колонок раздела «Помещения / Квартиры» между F5
// и повторными входами в раздел. ViewModel этого раздела регистрируется inTransientScope
// (пересоздаётся при каждом входе), поэтому хранить выбор в VM бессмысленно — используем
// localStorage напрямую в компоненте. Ключи уникальные для этого раздела (см. область в задаче:
// ТОЛЬКО «Помещения / Квартиры», другие гриды это не затрагивает).
const ABONENTS_COLUMN_VISIBILITY_STORAGE_KEY = 'osiAbonentsColumnVisibility';
// OSI-808: отдельный ключ — набор ИМЁН полей, которые пользователь хотя бы раз переключил вручную
// через меню «Столбцы». Раньше этот «ручной выбор» отслеживался двумя изолированными ref-флагами
// (OSI-788 — owner, OSI-805 — livingFact) только в памяти компонента; теперь это один общий набор,
// персистентный, покрывающий ЛЮБУЮ колонку (включая effectiveSquare, для которой такой защиты
// не было вовсе).
const ABONENTS_COLUMN_VISIBILITY_TOUCHED_STORAGE_KEY = 'osiAbonentsColumnVisibilityTouched';

// OSI-808: стартовые значения видимости колонок — используются и как дефолт useState,
// и как откат при любой порче данных в localStorage (битый JSON, чужой формат и т.п.).
const DEFAULT_COLUMN_VISIBILITY_MODEL: Record<string, boolean> = {
  ercAccount: false,
  // OSI-788: стартовое значение — скрыто; колонка появляется, если «Владелец» заполнен хотя бы
  // у одной строки (см. правило hasOwner в useEffect ниже).
  owner: false,
  // OSI-805: стартовое значение — скрыто; колонка появляется, если «Проживают» >= 1 хотя бы
  // у одной КВАРТИРЫ (см. правило hasLivingFact в useEffect ниже).
  livingFact: false,
  livingJur: false,
  floor: false
};

// OSI-808: безопасное чтение сохранённой модели видимости. Любая порча (невалидный JSON, чужой
// тип, отсутствие ключа) не должна ронять экран — откатываемся к дефолту. Мержим с дефолтом,
// чтобы поле, добавленное в колонки уже ПОСЛЕ того, как у пользователя сохранился старый JSON,
// не считалось «видимым» по умолчанию (отсутствие поля в модели MUI трактуется как «видимо»).
const readColumnVisibilityFromStorage = (): Record<string, boolean> => {
  try {
    const raw = localStorage.getItem(ABONENTS_COLUMN_VISIBILITY_STORAGE_KEY);
    // Возвращаем КОПИЮ модульной константы, а не саму ссылку: этот объект уходит в React-state
    // columnVisibilityModel, и если когда-нибудь его начнут мутировать на месте (а не через
    // {...prev}), утечка ссылки необратимо испортила бы дефолт для всей сессии приложения.
    if (!raw) return { ...DEFAULT_COLUMN_VISIBILITY_MODEL };
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { ...DEFAULT_COLUMN_VISIBILITY_MODEL };
    return { ...DEFAULT_COLUMN_VISIBILITY_MODEL, ...(parsed as Record<string, boolean>) };
  } catch (e) {
    logger.error('OSI-808: битые данные columnVisibilityModel в localStorage, откат к дефолту', e);
    // См. комментарий выше — та же причина отдавать копию, а не константу напрямую.
    return { ...DEFAULT_COLUMN_VISIBILITY_MODEL };
  }
};

// OSI-808: безопасное чтение набора «тронутых» пользователем полей — по тому же принципу,
// что и readColumnVisibilityFromStorage: любая порча данных откатывается к пустому набору,
// а не роняет экран.
const readTouchedColumnsFromStorage = (): string[] => {
  try {
    const raw = localStorage.getItem(ABONENTS_COLUMN_VISIBILITY_TOUCHED_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((field): field is string => typeof field === 'string');
  } catch (e) {
    logger.error('OSI-808: битые данные touched-полей columnVisibility в localStorage, откат к пустому набору', e);
    return [];
  }
};

const validatePhone = Yup.string()
  .min(16, tokens.common.formFields.phone.validation.tooShort)
  .max(16, tokens.common.formFields.phone.validation.error);

const validateIIN = Yup.string()
  .test(
    'idn-test',
    () => tokens.common.formFields.IDNOSI.validation.error,
    (value) => checkIIN(value as string) || checkBIN(value as string) || !value
  )
  .notRequired();

interface Props {
  onChangeAbonents?: (abonents: Abonent[]) => void;
}

export const OsiAbonentsWidget: React.FC<Props> = observer(({ onChangeAbonents }) => {
  const vm = useInjection<IOsiAbonentsWidgetViewModel>(IOsiAbonentsWidgetViewModelToken);
  const { t, getGridLocale: ggl } = useTranslation();

  // Фаза 2: хранение выбранного parentAbonentId из диалога AreaTypeSelect
  // Используем ref т.к. DataGrid inline edit не поддерживает setEditCellValue для non-editable колонок
  const pendingParentAbonentIdRef = useRef<number | null>(null);

  // Фаза 2: диалог перепривязки существующего помещения к другому родителю
  const [parentEditState, setParentEditState] = useState<{
    open: boolean; abonentId: number; currentParentId: number | null;
  }>({ open: false, abonentId: 0, currentParentId: null });

  useEffect(() => {
    if (!onChangeAbonents) return;

    onChangeAbonents(vm.abonents);
  }, [vm.abonents]);

  const { translateToken: tt, getGridLocale } = useTranslation();

  const apiRef = useGridApiRef();

  // OSI-808: динамическая видимость колонок (floor скрыт, effectiveSquare/owner/livingFact —
  // по наличию данных), но НАЧАЛЬНОЕ значение теперь читаем из localStorage лениво (функция
  // передана в useState, а не результат её вызова — читаем один раз при монтировании, а не
  // на каждый рендер). Это и есть восстановление набора столбцов после F5/повторного входа
  // в раздел из требования OSI-808(б).
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<Record<string, boolean>>(
    readColumnVisibilityFromStorage
  );

  // OSI-808: единый механизм «поле тронуто пользователем вручную», заменяющий собой два прежних
  // изолированных ref-флага (OSI-788 — isOwnerVisibilityManualRef, OSI-805 —
  // isLivingFactVisibilityManualRef). Семантика обоих правил сохранена и описана рядом с расчётом
  // hasOwner/hasLivingFact в useEffect ниже — сами флаги больше не нужны, т.к. это теперь частный
  // случай общего набора touchedColumns, который вдобавок покрывает ЛЮБУЮ колонку (в т.ч.
  // effectiveSquare, которая раньше затиралась автоматикой безусловно) и переживает F5.
  // Ref (Set), а не state — изменение набора само по себе не должно вызывать перерисовку;
  // лениво инициализируем один раз сохранённым набором из localStorage.
  const touchedColumnsRef = useRef<Set<string> | null>(null);
  if (touchedColumnsRef.current === null) {
    touchedColumnsRef.current = new Set(readTouchedColumnsFromStorage());
  }

  // OSI-808: сохранение набора «тронутых» полей в localStorage — без try/catch здесь падать
  // было бы некритично для отображения экрана, но лучше не терять выбор пользователя молча.
  const persistTouchedColumns = (touched: Set<string>) => {
    try {
      localStorage.setItem(ABONENTS_COLUMN_VISIBILITY_TOUCHED_STORAGE_KEY, JSON.stringify(Array.from(touched)));
    } catch (e) {
      logger.error('OSI-808: не удалось сохранить touched-поля columnVisibility в localStorage', e);
    }
  };

  // OSI-808: сохранение самой модели видимости — вызывается ТОЛЬКО из ручного изменения через
  // панель «Столбцы» (handleColumnVisibilityModelChange), а не из автоматического useEffect —
  // иначе автоматический пересчёт по данным перетирал бы сохранённый пользователем выбор в сторе.
  const persistColumnVisibilityModel = (model: Record<string, boolean>) => {
    try {
      localStorage.setItem(ABONENTS_COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(model));
    } catch (e) {
      logger.error('OSI-808: не удалось сохранить columnVisibilityModel в localStorage', e);
    }
  };

  React.useEffect(() => {
    // Скрываем effectiveSquare только если ни у одного абонента нет значения.
    // OSI-808: раньше это поле обновлялось БЕЗУСЛОВНО (без защиты от ручного выбора) — теперь
    // оно наравне с owner/livingFact участвует в общей проверке touchedColumns ниже.
    const hasEffectiveSquare = vm.abonents?.some((a: any) => a.effectiveSquare && a.effectiveSquare > 0) ?? false;
    // OSI-788: колонка «Владелец» скрыта по умолчанию, ТОЛЬКО если значение пусто у ВСЕХ строк.
    // Пустым считаем null/undefined/'' (в БД встречаются оба варианта пустоты).
    const hasOwner = vm.abonents?.some((a: any) => !isOwnerEmpty(a.owner)) ?? false;
    // OSI-805: колонка «Проживают» показывается, если хотя бы у одной КВАРТИРЫ значение >= 1.
    // Считаем ТОЛЬКО по areaTypeCode === RESIDENTIAL и намеренно игнорируем остальные типы:
    // арендаторам бэкенд искусственно проставляет livingFact = 1 при создании (AbonentSvc.AddArendator),
    // поэтому без фильтра по типу колонка вылезала бы примерно у 60 ОСИ, где реальных проживающих нет.
    const hasLivingFact =
      vm.abonents?.some((a: any) => a.areaTypeCode === AreaTypeCodes.RESIDENTIAL && a.livingFact >= 1) ?? false;

    const autoComputedVisibility: Record<string, boolean> = {
      effectiveSquare: hasEffectiveSquare,
      owner: hasOwner,
      livingFact: hasLivingFact
    };

    setColumnVisibilityModel((prev) => {
      const touched = touchedColumnsRef.current ?? new Set<string>();
      let changed = false;
      const next = { ...prev };
      // OSI-808: ручной выбор пользователя ВСЕГДА главнее автоматики — применяем пересчитанное
      // значение только к полям, которых нет в touchedColumns (пользователь их не трогал).
      // Это и есть общий механизм, заменяющий прежние точечные isOwnerVisibilityManualRef/
      // isLivingFactVisibilityManualRef, но с тем же эффектом: включённая/выключенная вручную
      // колонка не будет переключена обратно при каждой перезагрузке списка абонентов
      // (loadAbonents/reloadCb/импорт).
      for (const field of Object.keys(autoComputedVisibility)) {
        if (touched.has(field)) continue;
        if (next[field] !== autoComputedVisibility[field]) {
          next[field] = autoComputedVisibility[field];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [vm.abonents]);

  // OSI-808: перехватываем изменения модели видимости из меню «Столбцы» — фиксируем ЛЮБОЕ
  // поле, у которого сменилась видимость, как «тронутое» пользователем (обобщение прежних
  // отдельных проверок для owner/livingFact — см. комментарий у touchedColumnsRef), персистим
  // и набор тронутых полей, и саму модель видимости в localStorage, чтобы оба переживали F5
  // и повторный вход в раздел (VM этого раздела transient и состояние не хранит).
  const handleColumnVisibilityModelChange = (model: Record<string, boolean>) => {
    const touched = touchedColumnsRef.current ?? new Set<string>();
    // В модели MUI отсутствие поля означает «колонка видима», поэтому сравниваем через !== false.
    const changedFields = new Set([...Object.keys(columnVisibilityModel), ...Object.keys(model)]);
    changedFields.forEach((field) => {
      const wasVisible = columnVisibilityModel[field] !== false;
      const willBeVisible = model[field] !== false;
      if (wasVisible !== willBeVisible) {
        touched.add(field);
      }
    });
    touchedColumnsRef.current = touched;
    persistTouchedColumns(touched);

    setColumnVisibilityModel(model);
    persistColumnVisibilityModel(model);
  };

  const columns: GridColDef[] = [
    {
      field: 'flat',
      headerName: t('abonents:grid.number'),
      // ВАЖНО: отображаемый номер арендатора («Аренда(...)») считаем ТОЛЬКО в renderCell, а НЕ в valueGetter.
      // Причина порчи данных: колонка flat editable:true. В MUI X DataGrid (newEditingApi, GridEditModes.Row)
      // значение редактируемой ячейки берётся из valueGetter → при коммите строки (processRowUpdate →
      // formatAbonentRow → updateAbonent) в newRow.flat утекало ОТОБРАЖАЕМОЕ «Аренда(...)» и сохранялось в БД,
      // затирая сырой flat. Через renderCell показ считается из params.row (name/flat), а сохраняемое значение
      // ячейки остаётся сырым row.flat — порча прекращается, реактивный показ сохраняется.
      renderCell: (params: any) => {
        const row = params.row ?? {};
        // Вычисление отображаемого номера из ТЕКУЩЕЙ строки (не из params.value):
        let displayValue: any;
        if (row.areaTypeCode !== AreaTypeCodes.ARENDATOR) {
          // Не арендатор — показываем сырой flat как есть.
          displayValue = row.flat;
        } else if (typeof row.flat === 'string' && row.flat.startsWith('Аренда(')) {
          // Легаси-значение уже зашито в БД как «Аренда(...)» — не оборачиваем повторно (идемпотентность).
          displayValue = row.flat;
        } else {
          // Живой расчёт из текущего имени строки; пустое имя → откат к flat.
          const name = row.name && row.name.trim() ? row.name : row.flat;
          displayValue = `Аренда(${name})`;
        }
        // Фаза 2: визуальный отступ для дочерних помещений (PARKING/STORAGE с parentAbonentId)
        const isChild = !!row.parentAbonentId;
        return (
          <Box sx={{ pl: isChild ? 3 : 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isChild && <Typography variant="caption" color="text.secondary">└</Typography>}
            <Typography variant="body2">{displayValue}</Typography>
          </Box>
        );
      },
      flex: 2,
      type: 'string',
      editable: true,
      cellClassName: (params: GridCellParams<string>) => checkRequiredCell(params, apiRef),
      renderEditCell: renderRequiredEditCell,
      preProcessEditCellProps: async (params: any) => {
        if (!params.hasChanged) return { ...params.props };
        const isValid = !!params.props.value;

        return { ...params.props, error: !isValid };
      }
    },
    {
      field: 'id',
      headerName: t('abonents:grid.personalAcc'),
      width: 80,
      editable: false,
      valueFormatter: ({ value }: GridValueFormatterParams<any>) => {
        if (typeof value === 'string') {
          return '';
        }
        return value;
      }
    },
    {
      field: 'ercAccount',
      headerName: t('abonents:grid.ercAccount'),
      width: 80,
      editable: false
    },
    {
      field: 'square',
      headerName: t('abonents:grid.square'),
      width: 90,
      type: 'number',
      editable: true,
      cellClassName: (params: GridCellParams<number>) => checkRequiredCell(params, apiRef),
      renderEditCell: renderRequiredEditCell,
      preProcessEditCellProps: async (params: any) => {
        if (!params.hasChanged) return { ...params.props };
        const isValid = !!params.props.value && params.props.value > 0;

        return { ...params.props, error: !isValid };
      }
    },
    {
      field: 'effectiveSquare',
      headerName: t('abonents:grid.effectiveSquare'),
      headerAlign: 'center',
      width: 100,
      type: 'number',
      editable: true
    },
    {
      field: 'floor',
      headerName: t('abonents:grid.floor'),
      headerAlign: 'center',
      width: 80,
      type: 'number',
      editable: true
    },
    {
      field: 'name',
      headerName: t('abonents:grid.name'),
      flex: 3,
      editable: true
    },
    {
      field: 'owner',
      headerName: t('abonents:grid.owner'),
      flex: 3,
      editable: true,
      // OSI-788: перевод значения на казахский делаем ТОЛЬКО через valueFormatter (отображение),
      // и НИКОГДА через valueGetter. В этом гриде режим GridEditModes.Row + newEditingApi:
      // значение редактируемой ячейки берётся из valueGetter, поэтому перевод в valueGetter
      // утёк бы в processRowUpdate → payload и бэк вернул бы 400 на [Values("Собственник","Арендатор")]
      // (ровно так уже портилось поле flat — см. комментарий у колонки flat выше).
      valueFormatter: ({ value }: GridValueFormatterParams<any>) => translateOwner(value, t),
      // OSI-788: выбор из выпадающего списка без пустого пункта, по образцу AreaTypeSelect.
      renderEditCell: (params: GridRenderEditCellParams) => <OwnerSelect params={params} />
    },
    {
      field: 'idn',
      headerName: t('abonents:grid.idn'),
      width: 120,
      editable: true,
      preProcessEditCellProps: async (params: any) => {
        if (!params.hasChanged) return { ...params.props };
        const isValid = await validateIIN.isValid(params.props.value);

        return { ...params.props, error: !isValid };
      },
      valueParser: (value, params) => {
        const mask = IMask.createMask({
          mask: '############',
          definitions: {
            '#': /[0-9]/
          }
        });
        mask.resolve(value);
        return mask.value;
      }
    },
    {
      field: 'phone',
      headerName: t('abonents:grid.phone'),
      headerAlign: 'center',
      width: 140,
      valueFormatter: (params: any) => formatPhone(params.value),
      valueParser: (value, params) => {
        const mask = IMask.createMask({
          mask: '+7 7## ### ## ##',
          definitions: {
            '#': /[0-9]/
          }
        });
        mask.resolve(value);
        return mask.value;
      },
      preProcessEditCellProps: async (params: any) => {
        if (!params.hasChanged) return { ...params.props };
        const isValid = await validatePhone.isValid(params.props.value);

        return { ...params.props, error: !isValid };
      },
      // cellClassName: (params: GridCellParams<string>) => checkRequiredCell(params, apiRef),
      editable: true
    },
    {
      field: 'areaTypeCode',
      headerName: t('abonents:grid.areaType'),
      width: 100,
      editable: true,
      renderEditCell: (params: GridRenderEditCellParams) => <AreaTypeSelect params={params} vm={vm} parentIdRef={pendingParentAbonentIdRef} />,
      valueFormatter: ({ value }: GridValueFormatterParams<AreaTypeCodes>) => {
        if (value === AreaTypeCodes.NON_RESIDENTIAL) return t('abonents:areaTypes.nonResidental');
        if (value === AreaTypeCodes.RESIDENTIAL) return t('abonents:areaTypes.residental');
        if (value === AreaTypeCodes.ARENDATOR) return t('abonents:areaTypes.arendator');
        if (value === AreaTypeCodes.PARKING) return t('abonents:areaTypes.parking');
        if (value === AreaTypeCodes.STORAGE) return t('abonents:areaTypes.storage');

        return value;
      },
      cellClassName: (params: GridCellParams<AreaTypeCodes>) => checkRequiredCell(params, apiRef)
    },
    // Фаза 2: колонка номера родительского помещения (кликабельная для перепривязки)
    {
      field: 'parentAbonentId',
      headerName: t('abonents:grid.parentFlat'),
      width: 120,
      editable: false,
      renderCell: (params: any) => {
        const parentId = params.row?.parentAbonentId;
        const isChild = params.row?.areaTypeCode === 'PARKING' || params.row?.areaTypeCode === 'STORAGE';
        const parent = parentId ? vm.abonents.find((a: any) => a.id === parentId) : null;
        if (!isChild) return null;
        return (
          <Box
            sx={{ cursor: 'pointer', width: '100%', height: '100%', display: 'flex', alignItems: 'center', color: parent ? 'text.primary' : 'text.secondary', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => setParentEditState({ open: true, abonentId: params.row.id, currentParentId: parentId })}
          >
            {parent ? parent.flat : t('abonents:noParent')}
          </Box>
        );
      }
    },
    {
      field: 'livingFact',
      headerName: t('abonents:grid.livingFact'),
      width: 80,
      type: 'number',
      editable: true
    },
    {
      field: 'livingJur',
      headerName: t('abonents:grid.livingJur'),
      width: 80,
      type: 'number',
      editable: true
    }
  ];

  // OSI-788: owner в payload — строка «Собственник»/«Арендатор» либо null (пусто), см. formatAbonentRow
  const checkAbonent = (abonent: Omit<Abonent, 'owner'> & { osiId: number | null; owner: string | null }) => {
    if (!abonent.id) throw new Error(t('abonents:validation.abonentId'));

    if (!abonent.areaTypeCode) throw new Error(t('abonents:validation.area'));

    if (!abonent.flat) throw new Error(t('abonents:validation.flat'));

    if (!abonent.square) throw new Error(t('abonents:validation.square'));

    return true;
  };

  const processAddNewAbonent = async (row: GridRowModel<Abonent>, vm: IOsiAbonentsWidgetViewModel) => {
    try {
      const abonent = formatAbonentRow(row, vm.osiId);

      // Фаза 2: внедрить parentAbonentId из ref (выбран в диалоге AreaTypeSelect)
      if (pendingParentAbonentIdRef.current !== null) {
        abonent.parentAbonentId = pendingParentAbonentIdRef.current;
        pendingParentAbonentIdRef.current = null;
      }

      checkAbonent(abonent);

      delete abonent.id;

      const newAbonent = await createAbonent(abonent);

      vm.cancelCreating();
      vm.clearTemporaryRow();
      // OSI-806: вместо прямого vm.abonents.push(...) — метод VM, который помимо добавления в список
      // условно перезапрашивает osiInfo (флаг hasChildAbonents управляет показом чекбокса
      // «Свернуть дочерние помещения»). Запрос уходит только если создан PARKING/STORAGE либо сразу
      // указан parentAbonentId; ошибка обновления погашена внутри VM и сюда не долетает.
      vm.pushAbonent(newAbonent);

      return newAbonent;
    } catch (e) {
      logger.error(e);
      notifications.error(e);
      vm.cancelCreating();
      return null;
    }
  };

  const processRowUpdateHandler = async (
    newRow: GridRowModel<
      Abonent & {
        isNew?: boolean;
      }
    >,
    prevRow: GridRowModel<Abonent>
  ) => {
    if (_.isEqual(newRow, prevRow)) return prevRow;
    try {
      if (!vm.osiId) throw new Error(t('abonents:validation.osiId'));

      if (newRow?.isNew) {
        await processAddNewAbonent(newRow, vm);
        return prevRow;
      }

      const abonent = formatAbonentRow(newRow, vm.osiId);

      // Фаза 2: при редактировании — применить новый parentAbonentId из диалога (перепривязка)
      if (pendingParentAbonentIdRef.current !== null) {
        abonent.parentAbonentId = pendingParentAbonentIdRef.current;
        pendingParentAbonentIdRef.current = null;
      }

      if (!abonent.id) {
        throw new Error(t('abonents:validation.abonentId'));
      }

      await updateAbonent(abonent.id, abonent);
      const updated = await getAbonent(abonent.id);
      // OSI-806: vm.updateAbonent сам решает, нужно ли перезапросить osiInfo (сравнивает старый и
      // новый areaTypeCode/parentAbonentId). Смена типа помещения идёт тоже через этот обработчик.
      // Обновление osiInfo внутри fire-and-forget с собственным catch — в этот catch оно не попадёт
      // и не покажет notifications.error поверх уже успешного сохранения абонента.
      vm.updateAbonent(updated);
      notifications.success();
      return updated;
    } catch (e) {
      notifications.error(e);
      return prevRow;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 1 }} display="flex" alignItems="center">
        <Typography variant="h5">
          {tt(tokens.common.total)} : {vm.apartCount}
        </Typography>
        <OsiAddAbonentButton vm={vm} apiRef={apiRef} />
        <Button
          variant="outlined"
          color="primary"
          startIcon={<CloudDownloadIcon />}
          onClick={vm.downloadReport}
          sx={{ ml: 2 }}
        >
          <TranslatedToken id={tokens.common.download} />
        </Button>
        <ImportAbonentsButton vm={vm} />
      </Box>
      <Box sx={{ height: '65vh' }}>
        <DataGridPro
          rowHeight={28}
          sx={{
            '& .MuiDataGrid-cell--editing.required': {
              position: 'relative',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: '1px solid red'
              }
            },
            '& .Mui-error': {
              color: 'red'
            }
          }}
          apiRef={apiRef}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          loading={vm.isLoading}
          localeText={getGridLocale()}
          rows={vm.sortedAbonents}
          columns={columns as any}
          // Вариант А: sortingMode="server" отключает встроенную ПЛОСКУЮ сортировку грида (грид не
          // перетасовывает rows и НЕ шлёт сетевой запрос — данные уже загружены). Порядок строк целиком
          // формирует ViewModel.sortedAbonents: сортируются только родители, дети следуют за родителем.
          // Стрелка направления управляется контролируемым sortModel; клик по заголовку -> onSortModelChange.
          sortingMode="server"
          sortModel={vm.sortModel}
          onSortModelChange={vm.setSortModel}
          density="compact"
          disableSelectionOnClick
          onCellClick={(params) => {
            if (params.field === 'actions') {
              vm.startEditingAbonent(params.row);
            }
          }}
          processRowUpdate={processRowUpdateHandler}
          experimentalFeatures={{ newEditingApi: true }}
          onRowEditStop={(params: GridRowEditStopParams, event) => {
            // Сохранение по blur: если обязательные поля заполнены — разрешаем выход и сохранение
            if (params.reason === 'rowFocusOut') {
              const row = apiRef.current.unstable_getRowWithUpdatedValuesFromRowEditing(params.id);
              if (row.areaTypeCode && row.flat && row.square > 0) {
                // Обязательные поля заполнены — разрешаем processRowUpdate
                return;
              }
              // Обязательные поля не заполнены — остаёмся в редактировании
              event.defaultMuiPrevented = true;
              return;
            }
            // Escape и другие причины — стандартное поведение
            const savedRow = apiRef.current.unstable_getRowWithUpdatedValuesFromRowEditing(params.id);
            setTimeout(() => {
              vm.saveTemporaryRow(savedRow);
              vm.cancelCreating();
            });
          }}
          editMode={GridEditModes.Row}
          components={{
            Toolbar,
            Footer: StateFooter,
            LoadingOverlay: LinearProgress
          }}
          componentsProps={{
            // OSI-808: toolbar больше не принимает пропсов (vm ему не требовался и раньше,
            // apiRef тоже не использовался) — componentsProps.toolbar убран вместе с ToolbarProps.
            footer: { vm }
          }}
        />
      </Box>
      {/* Фаза 2: диалог перепривязки существующего помещения к другому родителю */}
      <Dialog
        open={parentEditState.open}
        onClose={() => setParentEditState({ open: false, abonentId: 0, currentParentId: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('abonents:selectParentTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('abonents:selectParentDescription')}
          </Typography>
          {vm.abonents
            .filter((a: any) => (a.areaTypeCode === 'RESIDENTIAL' || a.areaTypeCode === 'NON_RESIDENTIAL') && a.id !== parentEditState.abonentId)
            .map((a: any) => (
              <MenuItem
                key={a.id}
                selected={a.id === parentEditState.currentParentId}
                onClick={async () => {
                  // Обновляем parentAbonentId через API
                  try {
                    const abonent = formatAbonentRow(
                      vm.abonents.find((ab: any) => ab.id === parentEditState.abonentId)!,
                      vm.osiId
                    );
                    abonent.parentAbonentId = a.id;
                    await updateAbonent(abonent.id!, abonent);
                    const updated = await getAbonent(abonent.id!);
                    // OSI-806: смена родителя меняет hasChildAbonents — vm.updateAbonent обновит osiInfo сам.
                    vm.updateAbonent(updated);
                    notifications.success();
                  } catch (e) {
                    notifications.error(e);
                  }
                  setParentEditState({ open: false, abonentId: 0, currentParentId: null });
                }}
                sx={{ py: 1 }}
              >
                {a.flat} — {a.name || ''}
              </MenuItem>
            ))}
          <MenuItem
            onClick={async () => {
              // Убрать привязку
              try {
                const abonent = formatAbonentRow(
                  vm.abonents.find((ab: any) => ab.id === parentEditState.abonentId)!,
                  vm.osiId
                );
                abonent.parentAbonentId = null;
                await updateAbonent(abonent.id!, abonent);
                const updated = await getAbonent(abonent.id!);
                // OSI-806: снятие привязки к родителю может убрать последнее дочернее помещение —
                // vm.updateAbonent перезапросит osiInfo и чекбокс исчезнет без перезагрузки страницы.
                vm.updateAbonent(updated);
                notifications.success();
              } catch (e) {
                notifications.error(e);
              }
              setParentEditState({ open: false, abonentId: 0, currentParentId: null });
            }}
            sx={{ py: 1, color: 'text.secondary' }}
          >
            {t('abonents:noParent')}
          </MenuItem>
        </DialogContent>
      </Dialog>
    </Box>
  );
});

interface FooterProps {
  vm: IOsiAbonentsWidgetViewModel;
}

const StateFooter: React.FC<FooterProps> = observer(({ vm }) => (
  <Grid container spacing={2} direction="row">
    <Grid item xs={12} md={6}>
      <Stack sx={{ p: 1 }} direction="row">
        <TranslatedToken id={tokens.osiAbonents.totalSquare} /> -&nbsp;
        <Typography component="span" sx={{ pr: 2, color: 'primary.main' }}>
          {vm.totalSquare}
        </Typography>
        <Typography component="span" sx={{ color: 'primary.main' }}>
          {vm.totalParkingPlaces}
        </Typography>
      </Stack>
    </Grid>
    <Grid item xs={12} md={6}>
      <Stack sx={{ p: 1 }} direction="row" justifyContent="flex-end">
        <Typography>
          <TranslatedToken id={tokens.osiAbonents.totalRows} /> : {vm.apartCount}
        </Typography>
      </Stack>
    </Grid>
  </Grid>
));

// OSI-808: здесь НЕ должно быть observer. observer из mobx-react-lite оборачивает компонент
// в React.memo, а пропсы тулбара (раньше vm/apiRef из componentsProps.toolbar) стабильны
// по ссылке между рендерами родителя — из-за этого React.memo блокировал перерисовку самого
// Toolbar и вложенной GridToolbarColumnsButton. Кнопка «Столбцы» держала устаревший внутренний
// open=false и на КАЖДЫЙ клик снова открывала панель вместо того, чтобы закрыть её повторным
// кликом (баг воспроизводился именно так — проверено в браузере). В разделе «Сальдо» тот же
// сценарий работает корректно, потому что там тулбар (DataGridToolBar) объявлен без observer.
// vm в этом тулбаре и раньше нигде не использовался — убираем observer и параметр вместе.
const Toolbar: React.FC = () => (
  <GridToolbarContainer>
    {/* @ts-expect-error-next-line */}
    <GridToolbarColumnsButton />
    {/* @ts-expect-error-next-line */}
    <GridToolbarFilterButton />
  </GridToolbarContainer>
);

interface SelectProps {
  params: GridRenderEditCellParams;
  vm?: IOsiAbonentsWidgetViewModel;
  parentIdRef?: React.MutableRefObject<number | null>;
}

// OSI-788: редактор ячейки «Владелец» — выпадающий список по образцу AreaTypeSelect.
// В value ячейки (а значит и в payload) кладём СЫРУЮ русскую строку из OWNER_VALUES,
// в подписи пунктов — перевод по текущей локали (common:owner / common:arendator).
// Пустого пункта в списке нет (требование заказчика), поэтому очистить значение через
// выпадающий список нельзя — уже заполненный «Владелец» не может быть случайно обнулён.
export const OwnerSelect: React.FC<{ params: GridRenderEditCellParams }> = ({ params }) => {
  const { id, value, field, hasFocus } = params;
  const apiRef = useGridApiContext();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useLayoutEffect(() => {
    if (hasFocus) {
      inputRef.current?.focus();
    }
  }, [hasFocus]);

  const onChangeHandler = (e: any) => {
    apiRef.current.setEditCellValue({ id, field, value: e.target.value });
  };

  return (
    <TextField
      inputRef={inputRef}
      // У существующих строк значение может быть null/undefined — иначе MUI ругается
      // на переключение TextField из неконтролируемого в контролируемый.
      value={value ?? ''}
      fullWidth
      select
      onChange={onChangeHandler}
    >
      <MenuItem dense value={OWNER_VALUES.OWNER}>
        {t('common:owner')}
      </MenuItem>
      <MenuItem dense value={OWNER_VALUES.ARENDATOR}>
        {t('common:arendator')}
      </MenuItem>
    </TextField>
  );
};

// Фаза 2: при выборе PARKING/STORAGE — модальное окно выбора родительского помещения
export const AreaTypeSelect: React.FC<SelectProps> = observer(({ params, vm, parentIdRef }) => {
  const { id, value, field, cellMode, hasFocus, error } = params;
  const apiRef = useGridApiContext();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [parentDialogOpen, setParentDialogOpen] = useState(false);
  const [pendingAreaType, setPendingAreaType] = useState<string>('');

  const isError = !!error || (hasFocus && cellMode === GridCellModes.Edit && !value);

  useLayoutEffect(() => {
    if (hasFocus) {
      inputRef.current?.focus();
    }
  }, [hasFocus]);

  // Родительские помещения — любые кроме самого редактируемого. PARKING/STORAGE могут быть
  // дочерними к квартире/нежилому/паркингу/арендатору или корневыми (parent=NULL).
  const parentOptions = (vm?.abonents ?? []).filter((a: any) => a.id !== id);

  const onChangeHandler = (e: any) => {
    const newValue = e.target.value;
    const previousValue = value;

    apiRef.current.setEditCellValue({ id, field, value: newValue });

    // Для PARKING/STORAGE — открываем диалог выбора родителя ВСЕГДА (даже если
    // других помещений в ОСИ нет — пользователю даём опцию "Без родителя").
    if (newValue === AreaTypeCodes.PARKING || newValue === AreaTypeCodes.STORAGE) {
      setPendingAreaType(previousValue as string);
      setParentDialogOpen(true);
    } else {
      // Сбросить parentAbonentId если выбран не PARKING/STORAGE
      if (parentIdRef) parentIdRef.current = null;
    }
  };

  // Явный выбор пользователя: parent помещение или "Без родителя" (parentId=null).
  const handleParentSelect = (parentId: number | null) => {
    if (parentIdRef) parentIdRef.current = parentId;
    setParentDialogOpen(false);
  };

  // Закрытие диалога без явного выбора — откатываем тип к предыдущему.
  const handleDialogClose = () => {
    if (parentIdRef) parentIdRef.current = null;
    apiRef.current.setEditCellValue({ id, field, value: pendingAreaType || value });
    setParentDialogOpen(false);
  };

  return (
    <>
      <StyledTooltip open={!isOpenMenu && isError} title={t('abonents:validation.requiredField')}>
        <TextField
          SelectProps={{
            onOpen: () => setIsOpenMenu(true),
            onClose: () => setIsOpenMenu(false)
          }}
          inputRef={inputRef}
          value={value}
          fullWidth
          select
          onChange={onChangeHandler}
        >
          <MenuItem dense value={AreaTypeCodes.RESIDENTIAL}>
            {t('abonents:areaTypes.residental')}
          </MenuItem>
          <MenuItem dense value={AreaTypeCodes.NON_RESIDENTIAL}>
            {t('abonents:areaTypes.nonResidental')}
          </MenuItem>
          <MenuItem dense value={AreaTypeCodes.PARKING}>
            {t('abonents:areaTypes.parking')}
          </MenuItem>
        <MenuItem dense value={AreaTypeCodes.STORAGE}>
          {t('abonents:areaTypes.storage')}
        </MenuItem>
        {/* BASEMENT удалён бэкендом (OSI.Core dev 34d6a1f); ARENDATOR не выбираем в общей форме абонента. */}
      </TextField>
    </StyledTooltip>

    {/* Диалог выбора родителя для PARKING/STORAGE. Опция "Без родителя" — корневая парковка. */}
    <Dialog open={parentDialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('abonents:selectParentTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('abonents:selectParentDescription')}
        </Typography>
        <MenuItem onClick={() => handleParentSelect(null)} sx={{ py: 1, color: 'text.secondary' }}>
          {t('abonents:noParent')}
        </MenuItem>
        {parentOptions.map((a: any) => (
          <MenuItem key={a.id} onClick={() => handleParentSelect(a.id)} sx={{ py: 1 }}>
            {/* Фаза 2: родителем может быть арендатор — показываем flatDisplay (единый номер от backend), иначе сырой flat */}
            {(a.areaTypeCode === AreaTypeCodes.ARENDATOR ? (a.flatDisplay ?? a.flat) : a.flat)} —{' '}
            {a.name || t('abonents:grid.number')}
          </MenuItem>
        ))}
      </DialogContent>
    </Dialog>
    </>
  );
});
